// The app is rendered by a dynamically imported chunk (src/index.tsx). If that
// import itself fails, no React tree exists to show the root error boundary,
// and the page would stay white. Render a dependency-free fallback instead and
// tell the native shell, which otherwise keeps its startup cover up.
//
// Kept free of app imports on purpose (only the entry's own recovery module):
// this must work when everything else failed to load, and on old engines, so
// it uses only DOM APIs that exist in Chromium 66 / Safari 12 WebViews
// (no replaceChildren, no append).

import { getPreloadRecoveryState } from "./utils/vite-preload-error-recovery";
import { ENTRY_FAILURE_COPY, pickEntryFailureLanguage } from "./entry-failure-copy";

function describe(error: unknown): string {
  if (error instanceof Error) return (error.message || error.name || "Unknown error").slice(0, 300);
  try {
    return (typeof error === "string" ? error : JSON.stringify(error) || "Unknown error").slice(0, 300);
  } catch {
    return "Unknown error";
  }
}

function postToNativeShell(payload: { typeEvent: string; message: string | null }): void {
  try {
    window.ReactNativeWebView?.postMessage(JSON.stringify(payload));
  } catch {
    // The fallback is still usable in a plain browser.
  }
}

function reportEntryFailureToNativeShell(error: unknown): void {
  postToNativeShell({
    typeEvent: "web_content_error",
    message: JSON.stringify({
      kind: "chunk_load_failed",
      message: `App entry failed to load: ${describe(error)}`,
      stack: error instanceof Error ? (error.stack || "").slice(0, 2000) : "",
      route: (/^\/?([^/?#]*)/.exec(window.location.pathname || "")?.[1] ?? "").replace(/[^A-Za-z0-9_-]/g, "").slice(0, 40),
      readyState: document.readyState,
      source: "entry",
      line: null,
      column: null,
    }),
  });
}

function clearChildren(element: HTMLElement): void {
  while (element.firstChild) element.removeChild(element.firstChild);
}

/**
 * Called from `src/index.tsx` when the app entry import rejects. If the
 * preload recovery already requested a reload, the reload is the recovery;
 * rendering here would flash an error and tell the shell the page is ready
 * (dropping its cover and its readiness deadline for the reloaded document).
 * The shell's injected `vite:preloadError` listener already reported a chunk
 * miss, so the entry only reports when no preload error was observed.
 */
// A stalled deployment probe must not keep the user on a blank screen: after
// this long the fallback renders regardless (a late reload simply replaces it).
export const PENDING_RECOVERY_WAIT_MS = 10_000;

export async function handleAppEntryImportFailure(
  error: unknown,
  { pendingRecoveryWaitMs = PENDING_RECOVERY_WAIT_MS }: { pendingRecoveryWaitMs?: number } = {}
): Promise<void> {
  let recovery = getPreloadRecoveryState();
  if (recovery.pendingRecovery) {
    // The deployment probe is still deciding whether to reload; a fallback
    // now would race that navigation and drop the native cover for nothing.
    let timer: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([
      recovery.pendingRecovery.catch(() => false),
      new Promise<void>(resolve => {
        timer = setTimeout(resolve, pendingRecoveryWaitMs);
      }),
    ]).finally(() => clearTimeout(timer));
    recovery = getPreloadRecoveryState();
  }
  if (recovery.reloadRequested) return;
  renderEntryFailure(error, { reportToNativeShell: recovery.preloadErrorsObserved === 0 });
}

export function renderEntryFailure(
  error: unknown,
  { root = document.getElementById("root"), reportToNativeShell = true }: { root?: HTMLElement | null; reportToNativeShell?: boolean } = {}
): void {
  if (reportToNativeShell) reportEntryFailureToNativeShell(error);
  if (!root) return;
  try {
    renderFallbackInto(root);
  } catch {
    // Nothing is on screen; leave the native cover up so the shell's readiness
    // deadline can show its own retry screen.
    return;
  }
  // Only once something is on screen: drops the native startup cover so the
  // reload button is reachable.
  postToNativeShell({ typeEvent: "loaded_page", message: null });
}

function renderFallbackInto(root: HTMLElement): void {
  // Browser locale, like App.tsx getBrowserLanguage; index.html hardcodes
  // <html lang="en">, so the document language is not a signal.
  const language = pickEntryFailureLanguage([...(navigator.languages || []), navigator.language]);
  const [title, bodyCopy, reloadLabel] = ENTRY_FAILURE_COPY[language] || ENTRY_FAILURE_COPY.en;
  clearChildren(root);
  const container = document.createElement("div");
  container.setAttribute("role", "alert");
  container.style.cssText =
    "min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;box-sizing:border-box;background:#FCFFFD;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;text-align:center;";
  const heading = document.createElement("h1");
  heading.textContent = title;
  heading.style.cssText = "margin:0 0 16px;font-size:24px;line-height:1.25;font-weight:600;color:#121212;";
  const body = document.createElement("p");
  body.textContent = bodyCopy;
  body.style.cssText = "margin:0 0 24px;font-size:16px;line-height:24px;color:#3D3E3D;max-width:480px;";
  const button = document.createElement("button");
  button.type = "button";
  button.textContent = reloadLabel;
  button.style.cssText =
    "padding:12px 28px;border-radius:999px;border:none;background:#121212;color:#fff;font-size:16px;font-weight:600;cursor:pointer;";
  button.addEventListener("click", () => window.location.reload());
  const column = document.createElement("div");
  column.appendChild(heading);
  column.appendChild(body);
  column.appendChild(button);
  container.appendChild(column);
  root.appendChild(container);
}
